import type { InspectionFormState, InspectionFormAction } from "../useInspectionForm";

interface Props {
  state: InspectionFormState;
  dispatch: React.Dispatch<InspectionFormAction>;
}

const labelCls = "text-xs font-medium text-foreground-500 mb-1 block";
const selectCls = "w-full bg-white border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors appearance-none cursor-pointer";
const inputCls = "w-full bg-white border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors";

const REC_TYPES = [
  { value: "PODA_LIMPEZA", label: "Poda de limpeza" },
  { value: "PODA_FORMACAO", label: "Poda de formação" },
  { value: "PODA_ADEQUACAO", label: "Poda de adequação" },
  { value: "PODA_EMERGENCIAL", label: "Poda emergencial" },
  { value: "TRATAMENTO_FITOSSANITARIO", label: "Tratamento fitossanitário" },
  { value: "ADUBACAO", label: "Adubação" },
  { value: "TUTORAMENTO", label: "Tutoramento" },
  { value: "MONITORAMENTO", label: "Monitoramento" },
  { value: "TRANSPLANTIO", label: "Transplantio" },
  { value: "SUPRESSAO", label: "Supressão (remoção)" },
];

const PRIORITY_LEVELS = [
  { value: "URGENTE", label: "Urgente" },
  { value: "ALTA", label: "Alta" },
  { value: "MEDIA", label: "Média" },
  { value: "BAIXA", label: "Baixa" },
];

export default function StepParecer({ state, dispatch }: Props) {
  return (
    <div className="space-y-5">
      {/* Diagnosis */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4 space-y-3">
        <h4 className="text-sm font-semibold text-foreground-800 flex items-center gap-2">
          <i className="ri-stethoscope-line text-primary-500"></i>
          Diagnóstico e Parecer Técnico
        </h4>
        <div>
          <label className={labelCls}>Diagnóstico</label>
          <textarea
            rows={3}
            value={state.diagnosis}
            onChange={(e) => dispatch({ type: "SET_FIELD", field: "diagnosis", value: e.target.value })}
            placeholder="Diagnóstico técnico consolidado da árvore..."
            className={`${inputCls} resize-none`}
          />
        </div>
        <div>
          <label className={labelCls}>Parecer técnico</label>
          <textarea
            rows={3}
            value={state.technicalOpinion}
            onChange={(e) => dispatch({ type: "SET_FIELD", field: "technicalOpinion", value: e.target.value })}
            placeholder="Parecer técnico..."
            className={`${inputCls} resize-none`}
          />
        </div>
      </div>

      {/* Recommendations */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-foreground-800 flex items-center gap-2">
            <i className="ri-task-line text-accent-500"></i>
            Recomendações de Manejo
          </h4>
          <button
            type="button"
            onClick={() =>
              dispatch({ type: "ADD_RECOMMENDATION", rec: { recommendationType: "MONITORAMENTO" } })
            }
            className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
          >
            <i className="ri-add-line"></i> Adicionar recomendação
          </button>
        </div>

        {state.recommendations.length === 0 ? (
          <p className="text-xs text-foreground-400">Nenhuma recomendação registrada.</p>
        ) : (
          <div className="space-y-3">
            {state.recommendations.map((rec, idx) => {
              const isRemoval = rec.recommendationType === "SUPRESSAO";
              return (
                <div
                  key={idx}
                  className={`p-3 rounded-lg border ${
                    isRemoval ? "bg-danger-50/50 border-danger-200/50" : "bg-background-50 border-background-200/50"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-foreground-600">
                      Recomendação #{idx + 1}
                      {isRemoval && (
                        <span className="ml-2 px-2 py-0.5 bg-danger-100 text-danger-700 rounded-full text-[10px] font-bold">
                          SUPRESSÃO
                        </span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => dispatch({ type: "REMOVE_RECOMMENDATION", index: idx })}
                      className="text-xs text-danger-500 hover:text-danger-600"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Tipo</label>
                      <select
                        value={rec.recommendationType}
                        onChange={(e) =>
                          dispatch({ type: "UPDATE_RECOMMENDATION", index: idx, field: "recommendationType", value: e.target.value })
                        }
                        className={selectCls}
                      >
                        {REC_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Prioridade</label>
                      <select
                        value={rec.priority ?? ""}
                        onChange={(e) =>
                          dispatch({ type: "UPDATE_RECOMMENDATION", index: idx, field: "priority", value: e.target.value || null })
                        }
                        className={selectCls}
                      >
                        <option value="">—</option>
                        {PRIORITY_LEVELS.map((p) => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="mt-2">
                    <label className={labelCls}>
                      Justificativa {isRemoval && <span className="text-danger-500">*</span>}
                    </label>
                    <textarea
                      rows={2}
                      value={rec.justification ?? ""}
                      onChange={(e) =>
                        dispatch({ type: "UPDATE_RECOMMENDATION", index: idx, field: "justification", value: e.target.value || null })
                      }
                      placeholder={isRemoval ? "Justificativa técnica obrigatória para supressão..." : "Justificativa..."}
                      className={`${inputCls} resize-none ${isRemoval && !rec.justification ? "border-danger-300" : ""}`}
                    />
                    {isRemoval && !rec.justification && (
                      <p className="text-[10px] text-danger-500 mt-0.5">Justificativa obrigatória para recomendação de supressão.</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Compensation */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4 space-y-3">
        <h4 className="text-sm font-semibold text-foreground-800 flex items-center gap-2">
          <i className="ri-plant-line text-primary-500"></i>
          Compensação Ambiental
        </h4>

        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={state.compensation.compensationIndicated}
            onChange={(e) =>
              dispatch({ type: "SET_COMPENSATION", field: "compensationIndicated", value: e.target.checked })
            }
            className="accent-primary-500"
          />
          <span className="text-sm font-medium text-foreground-700">Compensação ambiental indicada</span>
        </label>

        {state.compensation.compensationIndicated && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-primary-50/30 rounded-lg border border-primary-200/50">
            <div>
              <label className={labelCls}>Quantidade de mudas propostas</label>
              <input
                type="number"
                min="0"
                value={state.compensation.proposedSeedlingQuantity ?? ""}
                onChange={(e) =>
                  dispatch({ type: "SET_COMPENSATION", field: "proposedSeedlingQuantity", value: e.target.value ? parseInt(e.target.value) : null })
                }
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Observações sobre compensação</label>
              <input
                type="text"
                value={state.compensation.notes ?? ""}
                onChange={(e) =>
                  dispatch({ type: "SET_COMPENSATION", field: "notes", value: e.target.value || null })
                }
                placeholder="Observações..."
                className={inputCls}
              />
            </div>
          </div>
        )}
      </div>

      {/* Monitoring */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={state.requiresMonitoring}
              onChange={(e) =>
                dispatch({ type: "SET_FIELD", field: "requiresMonitoring", value: e.target.checked })
              }
              className="accent-primary-500"
            />
            <span className="text-sm font-medium text-foreground-700">Requer monitoramento</span>
          </label>
          <div>
            <label className={labelCls}>Sugestão de próxima inspeção</label>
            <input
              type="date"
              value={state.nextInspectionSuggestedAt}
              onChange={(e) =>
                dispatch({ type: "SET_FIELD", field: "nextInspectionSuggestedAt", value: e.target.value })
              }
              className={inputCls}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
